import React from 'react';
import PropTypes from 'prop-types';
import {formatMoney} from '@shopify/theme-currency';

import {checkGifts} from './gift-with-purchase';
import CartItem from './CartItem';
import CartUpsells from './CartUpsells';

/* eslint-disable react/state-in-constructor, @shopify/react-prefer-private-members, @shopify/jsx-prefer-fragment-wrappers, @shopify/prefer-early-return, babel/camelcase */
const FreeShippingBar = ({
  total,
  threshold1,
  threshold2,
  freeShippingText,
  freeExpressShippingText,
  threshold1Text,
  threshold2Text,
}) => {
  //  Convert thresholds to cents so dollars can be used in shopify backend too specify
  // threshold amounts
  const thresholdOne = threshold1 * 100;
  const thresholdTwo = threshold2 * 100;

  /**
   * Calculate the percentage of the bar displaying
   * progress towards free shipping
   *
   * @return {string} percent of progress
   */
  const barPercentage = () => {
    if ((thresholdTwo && total >= thresholdTwo) || (!thresholdTwo && total >= thresholdOne)) return 100;
    return thresholdTwo && total >= thresholdOne ? (total / thresholdTwo) * 100 : (total / thresholdOne) * 100;
  };

  /**
   * Construct the message that the customer will see.
   *
   * @return {string} Message for customer
   */
  const message = () => {
    if (total < thresholdOne) {
      const leftAmount = formatMoney(thresholdOne - total, window.theme.moneyFormat);
      return threshold1Text.replace('{{ amount }}', leftAmount);
    } else if (thresholdTwo && total < thresholdTwo) {
      const leftAmount = formatMoney(thresholdTwo - total, window.theme.moneyFormat);
      return threshold2Text.replace('{{ amount }}', leftAmount);
    } else {
      return threshold2 ? freeExpressShippingText : freeShippingText;
    }
  };
  const barpercentage = barPercentage();
  const barWidth = {
    width: `${barpercentage}%`,
  };

  return (
    <div className="sidecart__free-shipping">
      <div className="sidecart__progress">
        <div className="bar" style={barWidth} />
      </div>
      <p>{message()}</p>
    </div>
  );
};

class SideCart extends React.Component {
  handleAddToCartEvent = async (
    evt,
    id,
    qty = 1,
    openCart = false,
    sellingPlan = null,
    subscriptionFrequency = null,
    subscriptionFrequencyUnits = null,
    productTitle = null,
    successCallback
  ) => {
    try {
      if (evt !== null) {
        await this.addLineItem(id, qty, sellingPlan, subscriptionFrequency, subscriptionFrequencyUnits, productTitle);
      }
      const cartContents = await this.fetchCart();
      const prevCartItemCount = this.state.itemsCount;

      this.updateCart(cartContents);

      // if evt = null then the cart update came from the wishlist modal
      if (evt === null) {
        // wait for this.state.itemsCount to update from within updateCart function before comparing
        setTimeout(() => {
          // compare cart item count in state to new cart item count in state
          if (prevCartItemCount < this.state.itemsCount) {
            // find the newly added product products (always first in cart items array
            const newCartItemsCount = this.state.itemsCount - prevCartItemCount;
            // store newly added cart items in array and remove from wishlist
            const itemsToRemoveFromWishlist = this.state.cartItems.slice(0, newCartItemsCount);
            if (itemsToRemoveFromWishlist && itemsToRemoveFromWishlist.length) {
              itemsToRemoveFromWishlist.forEach((item) => {
                window._swat.removeFromWishList({
                  epi: item.variant_id,
                  du: `${window.location.origin}${item.url}`,
                  empi: item.product_id,
                  iu: item.featured_image.url,
                  pr: item.price,
                  stk: 100,
                });
              });
            }
            if (openCart) {
              this.handleCartToggle();
            }
          }
        }, 1);
      }

      if (openCart && evt !== null) {
        this.handleCartToggle();
      }

      if (successCallback && typeof successCallback === 'function') {
        successCallback();
      }

      if (window.klaviyoCartTracking) {
        // Klaviyo "Added to Cart" tracking
        const cartTracking = {
          total_price: cartContents.total_price / 100,
          $value: cartContents.total_price / 100,
          total_discount: cartContents.total_discount,
          original_total_price: cartContents.original_total_price / 100,
          items: cartContents.items,
        };

        window.klaviyo.push(['track', 'Added to Cart', cartTracking]);
      }
    } catch (err) {
      window.console.error(err);
    }
  };

  addLineItem = async (id, qty, sellingPlan, subscriptionFrequency, subscriptionFrequencyUnits, productTitle) => {
    const data = {
      id,
      quantity: qty,
      properties: {},
    };
    if (sellingPlan) {
      data.selling_plan = sellingPlan;
    }

    if (subscriptionFrequency !== null) {
      // support legacy and new recharge
      if (sellingPlan) {
        data.properties._shipping_interval_frequency = subscriptionFrequency;
        data.properties._shipping_interval_unit_type = subscriptionFrequencyUnits;
      } else {
        data.properties.shipping_interval_frequency = subscriptionFrequency;
        data.properties.shipping_interval_unit_type = subscriptionFrequencyUnits;
      }
    }

    if (productTitle !== null) {
      data.properties._productTitle = productTitle;
    }
    const url = `${this.state.rootUrl}/cart/add.js`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('cart/add response was not OK');
      }
      /**
       * Send off dataLayer event for product added to cart
       */
      const product = await response.json();
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'twpAddToCart',
        ecommerce: {
          currencyCode: window.theme?.cart?.currency,
          add: {
            products: [
              {
                name: product.product_title,
                id: product.variant_id,
                price: product.final_price / 100,
                brand: product.vendor,
                category: product.product_type,
                quantity: product.quantity,
                delivery_frequency: product.properties?._shipping_interval_frequency
                  ? Number(product.properties._shipping_interval_frequency)
                  : null,
                delivery_frequency_unit_type: product.properties?._shipping_interval_unit_type ?? null,
                purchase_type: sellingPlan ? 'subscription' : 'one_time',
              },
            ],
          },
        },
      });
    } catch (err) {
      window.console.error(err);
    }
  };

  addLineItems = async (data, callback) => {
    const url = `${this.state.rootUrl}/cart/add.js`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('cart/add response was not OK');
      }
      if (typeof callback === 'function') {
        callback();
      }
    } catch (err) {
      window.console.error(err);
    }
  };

  updateLineItems = async (data, callback) => {
    const url = `${this.state.rootUrl}/cart/update.js`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('cart/update response was not OK');
      }
      if (typeof callback === 'function') {
        callback();
      }
      const json = await response.json();
      return json;
    } catch (err) {
      window.console.error(err);
    }
  };

  fetchCart = async () => {
    const url = `${this.state.rootUrl}/cart.js`;
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('Fetch cart response was not OK');
      }
      const json = await response.json();
      return json;
    } catch (err) {
      window.console.error(err);
    }
  };

  fetchCartCustomData = async () => {
    const url = `${this.state.rootUrl}/cart?view=custom-data`;

    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('Fetch cart custom data response was not OK');
      }
      const json = await response.json();
      return json;
    } catch (err) {
      window.console.error(err);
    }
  };

  handleCartToggle = (event) => {
    if (event) {
      event.preventDefault();
    }
    const cartOpenButton = document.querySelector('.header__btn--cart');
    const cartCloseButton = document.querySelector('.sidecart__close');
    if (document.body.classList.contains('cart-is-visible')) {
      document.body.classList.remove('cart-is-visible');
      if (cartOpenButton) {
        cartOpenButton.focus();
      }
      document.dispatchEvent(new CustomEvent('side-cart:closed'));
    } else {
      document.body.classList.add('cart-is-visible');
      if (cartCloseButton) {
        cartCloseButton.focus();
      }
      document.dispatchEvent(new CustomEvent('side-cart:open'));
    }
  };

  updateCart = () => {
    Shopify.getCart(
      function (event) {
        this.setState({
          itemsCount: event.item_count,
          total: event.total_price,
          totalDiscount: event.total_discount,
          cartItems: event.items,
        });
        const headerCounter = document.querySelector('[data-items-count]');
        if (headerCounter) {
          headerCounter.innerHTML = event.item_count;
        }
        if (event.items && event.items.length) {
          this.getUpsellsOrRecommendations(event.items)
            .then((result) => this.setState({upsellItems: result}))
            .catch((err) => window.console.log(`Failed to fetch Upsells, ${err}`));
        }
      }.bind(this)
    );
  };

  getProductByHandle = async (handle) => {
    try {
      const url = `${this.state.rootUrl}/products/${handle}.js`;
      const options = {
        headers: {'Content-Type': 'application/json'},
      };
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('Fetch response was not OK');
      }
      const json = await response.json();
      return json;
    } catch (err) {
      window.console.error(`Could not get product data for product with handle ${handle}`);
    }
  };

  getUpsellsFromProduct = async (product) => {
    // Cap on Upsell Products to display
    const maxUpsellProducts = 3;
    const upsellProducts = [];

    const upsellTags = product.tags?.filter((tag) => tag.indexOf('Upsell') !== -1);
    if (!upsellTags.length) return false;

    const handles = upsellTags.map((tag) => tag.split('|')[1]);
    if (!handles.length) return false;

    const uniqueUpsellsHandles = [...new Set(handles)];

    for (const handle of uniqueUpsellsHandles) {
      if (upsellProducts.length >= maxUpsellProducts) break;
      const upsellProduct = await this.getProductByHandle(handle);
      upsellProducts.push(upsellProduct);
    }

    return upsellProducts;
  };

  getCartUpsells = async (items) => {
    if (!items.length) return;
    const totalUpsellProducts = [];
    const productsToDisplay = [];
    // Cap on how many CartItems to try for Custom Upsells
    const maxItemsToTry = 3;
    let iterations = 0;
    // First get Custom Cart Item Upsells
    for (const item of items) {
      if (iterations >= maxItemsToTry) break;
      let handle = item.handle;

      // Skip GWP free products
      if (item.price === 0) continue;

      // If this is a subscription product, normalise the handle and fetch data for new handle
      const isSubscriptionProduct = item?.properties?.shipping_interval_frequency || item?.selling_plan_allocation;
      if (isSubscriptionProduct && item.handle.indexOf('-1') !== -1) {
        handle = item.handle.split(-1)[0];
      }

      if (sessionStorage.getItem(`__upsell:${handle}`)) {
        const upsells = JSON.parse(sessionStorage.getItem(`__upsell:${handle}`));
        totalUpsellProducts.push(upsells);
      } else {
        const upsellParentProduct = await this.getProductByHandle(handle);
        if (!upsellParentProduct) continue;
        const upsellProducts = await this.getUpsellsFromProduct(upsellParentProduct);
        if (upsellProducts) {
          sessionStorage.setItem(`__upsell:${handle}`, JSON.stringify(upsellProducts));
          totalUpsellProducts.push(upsellProducts);
        }
      }
      iterations++;
    }

    productsToDisplay.push(...totalUpsellProducts.flat());
    return productsToDisplay;
  };

  getShopifyRecommendations = async (items) => {
    if (!items.length) return;
    const withoutFreeItems = items.filter((item) => Number(item.price) > 0);
    if (!withoutFreeItems.length) return;
    const item = withoutFreeItems[0];
    let {handle, product_id} = item;

    // If this is a subscription product, normalise the handle and fetch data for new handle
    const isSubscriptionProduct = item?.properties?.shipping_interval_frequency || item?.selling_plan_allocation;
    if (isSubscriptionProduct && item.handle.indexOf('-1') !== -1) {
      handle = item.handle.split(-1)[0];
      const static_product = await this.getProductByHandle(handle);
      product_id = static_product.id;
    }
    let upsellsRecommendations = sessionStorage.getItem(`__recommendations:${handle}`);
    if (upsellsRecommendations) {
      upsellsRecommendations = JSON.parse(upsellsRecommendations);
    } else {
      const url = `${this.state.rootUrl}/recommendations/products.json?product_id=${product_id}`;
      const options = {
        headers: {'Content-Type': 'application/json'},
      };
      try {
        const response = await fetch(url, options);
        if (response.ok) {
          const recommendations = await response.json();
          const recommendationsWithoutFreeProducts = recommendations.products.filter(
            (recommendation) => Number(recommendation.price) > 0
          );
          upsellsRecommendations = recommendationsWithoutFreeProducts;
          sessionStorage.setItem(`__recommendations:${handle}`, JSON.stringify(upsellsRecommendations));
        } else {
          upsellsRecommendations = [];
        }
      } catch (err) {
        window.console.error(err);
      }
    }
    return upsellsRecommendations;
  };

  getUpsellsOrRecommendations = async (items) => {
    const itemIdsInCart = items.map((item) => item.product_id);
    const upsells = await this.getCartUpsells(items);
    let results = [];

    if (upsells && upsells.length < 3) {
      const recommendations = await this.getShopifyRecommendations(items);
      results = [...upsells, ...recommendations];
    } else {
      results = [...upsells];
    }

    results = results.filter((result) => result && !itemIdsInCart.includes(result.id));
    if (results.length > 3) results.length = 3;
    return results;
  };

  getGiftWithPurchases = async () => {
    const url = `${this.state.rootUrl}/cart?section_id=gift-with-purchase`;
    try {
      const response = await fetch(url);
      if (response.ok === false) {
        throw new Error('gift with purchase response was not OK');
      }
      const data = await response.text();
      const fragment = document.createRange().createContextualFragment(data);
      const json = JSON.parse(
        fragment?.querySelector('[data-gift-with-purchase')?.innerText || '{"gifts": [],"giftsInCart": []}'
      );
      this.setState({
        giftProducts: json,
      });
    } catch (err) {
      window.console.error(err);
    }
  };

  constructor(props) {
    super(props);
    window.sideCart = this;
    this.state = {
      itemsCount: window.theme.cart ? window.theme.cart.item_count : 0,
      totalDiscount: window.theme.cart ? window.theme.cart.total_discount : 0,
      total: window.theme.cart ? Number(window.theme.cart.total_price) : 0,
      showShippingTaxNotice: window.theme.shipping_taxes_note_enabled,
      freeShippingBar: window.theme.freeShipping && window.theme.freeShipping.enabled,
      shippingPriceDisplay: window.theme.shipping_price_enabled,
      shippingCheckoutText: window.theme.strings.shipping_at_checkout,
      threshold1:
        window.theme.freeShipping && window.theme.freeShipping.threshold1
          ? Number(window.theme.freeShipping.threshold1)
          : 0,
      threshold2:
        window.theme.freeShipping && window.theme.freeShipping.threshold2
          ? Number(window.theme.freeShipping.threshold2)
          : null,
      freeShippingText: window.theme.strings.free_shipping_text,
      freeExpressShippingText: window.theme.strings.free_express_shipping_text,
      threshold1Text: window.theme.strings.free_shipping_threshold_text,
      threshold2Text: window.theme.strings.free_shipping_threshold2_text,
      cartItems: window.theme.cart ? window.theme.cart.items : [],
      cartCustomData: {},
      rootUrl: window.theme.locale && !window.theme.locale.primary ? window.theme.locale.rootUrl : '',
      upsellItems: [],
      giftProducts: JSON.parse(
        document.querySelector('[data-gift-with-purchase]')?.innerText || '{"gifts": [],"giftsInCart": []}'
      ),
    };
  }

  componentDidMount() {
    const cartToggle = document.querySelectorAll('[data-toggle-cart]');
    if (cartToggle.length > 0) {
      cartToggle.forEach((el) => el.addEventListener('click', this.handleCartToggle));
    }

    if (this.state.cartItems && this.state.cartItems.length) {
      this.getUpsellsOrRecommendations(this.state.cartItems)
        .then((result) => this.setState({upsellItems: result}))
        .catch((err) => window.console.log(`Failed to fetch Upsells, ${err}`));
    }

    checkGifts(this.state.giftProducts, this.addLineItems, this.updateLineItems, () => this.handleAddToCartEvent(null));

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (target.classList.contains('swym-close-btn') || target.classList.contains('swym-modal-bg')) {
        this.handleAddToCartEvent(null, null, null, window.location.pathname !== '/cart');
      }
    });

    if (window.theme.isB2BStore) {
      this.fetchCartCustomData()
        .then((result) => this.setState({cartCustomData: result}))
        .catch((err) => window.console.log(`Failed to fetch cart custom data, ${err}`));
    }
  }

  componentDidUpdate(_, prevState) {
    if (this.state.cartItems !== prevState.cartItems) {
      this.getGiftWithPurchases();
    }

    if (this.state.giftProducts !== prevState.giftProducts) {
      checkGifts(this.state.giftProducts, this.addLineItems, this.updateLineItems, () =>
        this.handleAddToCartEvent(null)
      );
    }

    if (window.theme.isB2BStore) {
      const currentCartItems = this.state.cartItems.map((item) => item.id);
      const prevCartItems = prevState.cartItems.map((item) => item.id);
      const hasNewCartItems = currentCartItems.some((item) => !prevCartItems.includes(item));

      if (hasNewCartItems) {
        this.fetchCartCustomData()
          .then((result) => this.setState({cartCustomData: result}))
          .catch((err) => window.console.log(`Failed to fetch cart custom data, ${err}`));
      }
    }
  }

  render() {
    const {
      itemsCount,
      totalDiscount,
      total,
      freeShippingBar,
      shippingPriceDisplay,
      shippingCheckoutText,
      threshold1,
      threshold2,
      freeShippingText,
      freeExpressShippingText,
      threshold1Text,
      threshold2Text,
      cartItems,
      cartCustomData,
      rootUrl,
    } = this.state;

    const isCartPage = window.location.pathname === '/cart' || window.location.pathname === '/cart/';
    const upsellsEnabled = window.theme.upsells.cart_upsells;
    // EXPRESS PAYMENT BUTTONS
    // const containsSubscriptionProducts =
    //   cartItems &&
    //   cartItems.length &&
    //   cartItems.some(
    //     (item) =>
    //       item.properties &&
    //       item.properties.shipping_interval_frequency &&
    //       item.properties.shipping_interval_frequency !== null
    //   );

    return (
      <>
        {isCartPage && (
          <div className="page-header">
            <h1 className="h4">
              {window.theme.strings.title}: {itemsCount}
            </h1>
          </div>
        )}
        <div className="sidecart__header">
          <button
            type="button"
            className="sidecart__close"
            aria-label={window.theme.strings.closeCart}
            data-toggle-cart
          >
            <svg
              className="icon icon-close"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path
                d="M17,7,7,17"
                fill="none"
                stroke="currentColor"
                strokeLinecap="square"
                strokeWidth="1px"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d="M7,7,17,17"
                fill="none"
                stroke="currentColor"
                strokeLinecap="square"
                strokeWidth="1px"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </button>
          <p className="visually-hidden h4">{window.theme.strings.cartTitle}</p>
          <div className="sidecart__items-total">
            <svg
              className="icon icon-bag"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.45455 6L3 13.5H13L12.5455 6H3.45455Z"
                stroke="currentColor"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.00474 3L7.92049 3.00353C7.1285 3.07417 6.37564 4.22918 6 6L10 5.99967C9.61021 4.16638 8.82265 3.00436 8.00474 3Z"
                stroke="currentColor"
              />
            </svg>
            {itemsCount}
          </div>
        </div>
        {freeShippingBar && (
          <FreeShippingBar
            total={total}
            threshold1={threshold1}
            threshold2={threshold2}
            freeShippingText={freeShippingText}
            freeExpressShippingText={freeExpressShippingText}
            threshold1Text={threshold1Text}
            threshold2Text={threshold2Text}
          />
        )}

        <div className="side-cart__content-container">
          <div className="side-cart__content">
            {isCartPage && (
              <div className="cart-order__header medium-down--hide">
                <div className="cart-order__heading cart-order__heading--item">{window.theme.strings.item}</div>
                <div className="cart-order__heading cart-order__heading--qty">{window.theme.strings.qty}</div>
                <div className="cart-order__heading cart-order__heading--price">{window.theme.strings.price}</div>
              </div>
            )}
            <ul className="cart-items__container list-none">
              {cartItems.length ? (
                cartItems.map((item) => {
                  const customData = cartCustomData?.items?.find((customItem) => customItem.id === item.id) ?? {};
                  return (
                    <CartItem
                      item={item}
                      customData={customData}
                      updateCart={this.updateCart}
                      key={item.variant_id + item.quantity}
                      isCartPage={isCartPage}
                    />
                  );
                })
              ) : (
                <li className="side-cart__empty">{window.theme.strings.cart_empty}</li>
              )}
            </ul>
          </div>
          {upsellsEnabled && !isCartPage && this.state.cartItems && (
            <CartUpsells
              className={this.state.total === 0 ? 'hide' : ''}
              total={this.state.total}
              products={this.state.upsellItems}
              addToCart={this.handleAddToCartEvent}
            />
          )}
          <div className="side-cart__totals">
            {isCartPage && (
              <div className="cart-order__header">
                <div className="cart-order__heading cart-order__heading--item">
                  {window.theme.strings.order_summary}
                </div>
              </div>
            )}
            <div className="side-cart__subtotals">
              {totalDiscount > 0 && (
                <div className="side-cart__subtotal">
                  <span>{window.theme.strings.discounts}</span>
                  <span>
                    -{formatMoney(totalDiscount, window.theme.moneyFormat)}&nbsp;{window.Shopify.currency.active}
                  </span>
                </div>
              )}
              <div className="side-cart__subtotal">
                <span>{window.theme.strings.subtotal}</span>
                <span>
                  {formatMoney(total, window.theme.moneyFormat)}&nbsp;{window.Shopify.currency.active}
                </span>
              </div>
              {shippingPriceDisplay ? (
                <div className="side-cart__subtotal">
                  <span>{window.theme.strings.shipping}</span>
                  <span className="side-cart__subtotal-value">
                    {total < threshold1 * 100
                      ? formatMoney(window.theme.shipping_price, window.theme.moneyFormat)
                      : formatMoney(0, window.theme.moneyFormat)}
                    &nbsp;{window.Shopify.currency.active}
                  </span>
                </div>
              ) : (
                <div className="side-cart__shipping-message">
                  {(isCartPage || this.state.showShippingTaxNotice) && shippingCheckoutText}
                </div>
              )}
            </div>
            <div className="side-cart__checkout">
              <a
                href={`${rootUrl}/checkout`}
                className="btn btn--primary"
                disabled={!itemsCount > 0}
                data-checkout-button
              >
                {window.theme.strings.checkout}
              </a>
              <a href="/cart" className="btn btn--text btn--text-underline btn--view-bag">
                {window.theme.strings.view_bag}
              </a>
            </div>

            {/* EXPRESS PAYMENT BUTTONS

            {isCartPage && itemsCount > 0 && (
              <>
                <div className={`side-cart__express-payments ${containsSubscriptionProducts ? 'visually-hidden' : ''}`}>
                  <button type="button" className="btn btn--secondary btn--express-payment shop">
                    <svg width="64" height="24" viewBox="0 0 64 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M35.0408 10.251C35.0408 12.3897 33.5466 13.9164 31.4585 13.9164H29.4913C29.4696 13.9164 29.4481 13.9207 29.428 13.9291C29.408 13.9375 29.3897 13.9497 29.3744 13.9652C29.3591 13.9806 29.347 13.9989 29.3387 14.0191C29.3305 14.0393 29.3263 14.0609 29.3264 14.0827V16.8936C29.3264 16.9375 29.3091 16.9797 29.2782 17.0108C29.2473 17.042 29.2053 17.0596 29.1616 17.0598H27.785C27.7523 17.0596 27.7203 17.0498 27.6931 17.0315C27.6659 17.0132 27.6447 16.9873 27.6321 16.957C27.6238 16.9372 27.6195 16.9159 27.6193 16.8944V6.75108C27.6192 6.72929 27.6234 6.70769 27.6316 6.68752C27.6399 6.66736 27.652 6.64902 27.6673 6.63358C27.6826 6.61813 27.7008 6.60587 27.7209 6.59751C27.741 6.58914 27.7625 6.58484 27.7842 6.58484H31.4673C33.5466 6.58243 35.0408 8.10914 35.0408 10.251V10.251ZM33.3241 10.251C33.3241 9.02147 32.4885 8.12279 31.3553 8.12279H29.4913C29.4696 8.12279 29.4481 8.1271 29.428 8.13546C29.408 8.14383 29.3897 8.15608 29.3744 8.17153C29.3591 8.18698 29.347 8.20531 29.3387 8.22548C29.3305 8.24564 29.3263 8.26724 29.3264 8.28903V12.2034C29.3264 12.2473 29.3438 12.2893 29.3747 12.3204C29.4056 12.3514 29.4476 12.3688 29.4913 12.3688H31.3553C32.4885 12.3769 33.3241 11.4774 33.3241 10.251ZM35.4489 15.1139C35.4347 14.7859 35.5023 14.4595 35.6457 14.1644C35.7891 13.8693 36.0037 13.6148 36.2701 13.4241C36.8071 13.0185 37.6467 12.8089 38.8792 12.7656L40.1917 12.719V12.3303C40.1917 11.5513 39.6715 11.222 38.8359 11.222C38.0004 11.222 37.4706 11.5191 37.3489 12.005C37.339 12.0392 37.3181 12.0691 37.2894 12.09C37.2607 12.111 37.2259 12.1217 37.1905 12.1207H35.8939C35.8701 12.1213 35.8463 12.1167 35.8245 12.1071C35.8026 12.0975 35.7831 12.0832 35.7673 12.0652C35.7516 12.0471 35.74 12.0259 35.7334 12.0028C35.7268 11.9798 35.7253 11.9556 35.7291 11.9319C35.9211 10.7803 36.8759 9.90489 38.8952 9.90489C41.0433 9.90489 41.8188 10.9088 41.8188 12.825V16.8936C41.8184 16.9379 41.8004 16.9803 41.7689 17.0115C41.7374 17.0426 41.6949 17.06 41.6507 17.0598H40.3438C40.3 17.0597 40.2581 17.0424 40.2269 17.0116C40.2116 16.9962 40.1995 16.9779 40.1913 16.9578C40.183 16.9377 40.1788 16.9161 40.1789 16.8944V16.59C40.1794 16.5648 40.1722 16.5401 40.1584 16.5191C40.1446 16.4981 40.1248 16.4818 40.1016 16.4723C40.0784 16.4628 40.0528 16.4606 40.0284 16.466C40.0039 16.4714 39.9816 16.4841 39.9644 16.5024C39.5723 16.9305 38.9392 17.2397 37.9252 17.2397C36.4334 17.2397 35.4489 16.4607 35.4489 15.1139V15.1139ZM40.1917 14.232V13.9164L38.4918 14.0064C37.5962 14.0521 37.0752 14.4256 37.0752 15.0544C37.0752 15.623 37.553 15.9379 38.3878 15.9379C39.5218 15.9379 40.1917 15.3235 40.1917 14.232V14.232ZM43.1313 20.1341V18.9551C43.1316 18.9304 43.1372 18.906 43.1479 18.8838C43.1586 18.8615 43.174 18.8418 43.1931 18.8262C43.2122 18.8106 43.2345 18.7993 43.2584 18.7933C43.2823 18.7873 43.3072 18.7866 43.3314 18.7913C43.5315 18.829 43.7356 18.8483 43.9397 18.8491C44.2905 18.8662 44.6369 18.7648 44.9235 18.5609C45.2101 18.3571 45.4202 18.0627 45.5203 17.7248L45.6051 17.4533C45.6175 17.4169 45.6175 17.3773 45.6051 17.3409L42.8816 10.3217C42.8718 10.2963 42.8682 10.2688 42.8713 10.2417C42.8744 10.2145 42.8839 10.1886 42.8992 10.166C42.9145 10.1434 42.935 10.1249 42.959 10.112C42.9829 10.0991 43.0097 10.0922 43.0369 10.092H44.3574C44.3912 10.0924 44.424 10.1029 44.4517 10.1222C44.4794 10.1416 44.5006 10.1688 44.5127 10.2004L46.3623 15.1508C46.3738 15.1837 46.3953 15.2122 46.4237 15.2323C46.4521 15.2524 46.486 15.2633 46.5207 15.2633C46.5555 15.2633 46.5894 15.2524 46.6178 15.2323C46.6461 15.2122 46.6676 15.1837 46.6792 15.1508L48.2855 10.2141C48.2966 10.1803 48.3181 10.151 48.3468 10.1302C48.3755 10.1095 48.4101 10.0983 48.4455 10.0984H49.8117C49.8391 10.0986 49.866 10.1054 49.8902 10.1183C49.9143 10.1312 49.935 10.1497 49.9505 10.1723C49.966 10.195 49.9758 10.2211 49.9791 10.2483C49.9824 10.2756 49.979 10.3032 49.9693 10.3289L47.0561 18.1239C46.3855 19.9357 45.2354 20.3991 43.9693 20.3991C43.7292 20.4055 43.4891 20.3766 43.257 20.3132C43.2184 20.3029 43.1846 20.2794 43.1616 20.2466C43.1387 20.2139 43.1279 20.174 43.1313 20.1341V20.1341ZM18.7733 6.00017C17.0382 5.98777 15.3644 6.64306 14.0961 7.83126C14.0419 7.88111 14.0081 7.94942 14.0013 8.0229C13.9945 8.09639 14.0152 8.16978 14.0593 8.2288L14.8444 9.30497C14.8699 9.3408 14.9029 9.3706 14.941 9.39227C14.9792 9.41394 15.0216 9.42695 15.0653 9.4304C15.109 9.43384 15.153 9.42764 15.194 9.41221C15.2351 9.39678 15.2723 9.37251 15.303 9.34111C15.7591 8.88361 16.3014 8.5219 16.8983 8.27714C17.4953 8.03238 18.1348 7.90947 18.7797 7.91559C21.472 7.91559 22.8782 9.94906 22.8782 11.9568C22.8782 14.1397 21.4007 15.6752 19.2775 15.7058C17.6368 15.7058 16.4003 14.6143 16.4003 13.1727C16.402 12.8086 16.4842 12.4493 16.641 12.1208C16.7978 11.7924 17.0253 11.503 17.3071 11.2734C17.3666 11.2235 17.4043 11.1521 17.4119 11.0746C17.4195 10.9971 17.3965 10.9196 17.3479 10.859L16.5227 9.81334C16.4978 9.78171 16.4668 9.75543 16.4315 9.73603C16.3963 9.71664 16.3575 9.70452 16.3175 9.70041C16.2775 9.69629 16.2371 9.70025 16.1987 9.71206C16.1603 9.72387 16.1246 9.74329 16.0937 9.76917C15.587 10.1789 15.1781 10.6972 14.8969 11.2861C14.6158 11.8751 14.4696 12.5197 14.4691 13.1727C14.4691 15.6544 16.5707 17.6011 19.2631 17.6172H19.2991C22.4956 17.5754 24.807 15.1998 24.807 11.9528C24.807 9.06966 22.6909 6.00017 18.7741 6.00017H18.7733Z"
                        fill="black"
                      />
                    </svg>
                  </button>
                  <button type="button" className="btn btn--secondary btn--express-payment apple">
                    <svg width="64" height="24" viewBox="0 0 64 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M15 11.9149C15.0181 11.7873 15.0339 11.6572 15.0552 11.5288C15.1466 10.9764 15.3373 10.4618 15.669 10.0071C16.0559 9.46913 16.6211 9.08576 17.264 8.92516C17.7589 8.79356 18.2412 8.83453 18.7163 9.01184L19.4382 9.28135C19.6557 9.36251 19.8739 9.3633 20.0922 9.28371C20.3523 9.18915 20.61 9.08907 20.8716 8.99844C21.253 8.86527 21.6439 8.78174 22.0521 8.81562C22.6825 8.87 23.2538 9.07409 23.7172 9.52248C23.8527 9.65329 23.9662 9.80617 24.0875 9.94565C23.9528 10.0528 23.8228 10.1434 23.7061 10.2475C23.1986 10.7045 22.8913 11.264 22.8519 11.9551C22.8251 12.4161 22.8897 12.8629 23.0986 13.2829C23.346 13.7786 23.729 14.1387 24.2097 14.4019C24.2357 14.4161 24.2617 14.4311 24.2869 14.4429C24.398 14.4981 24.3972 14.4981 24.3602 14.6124C24.2436 14.9709 24.0883 15.3137 23.9031 15.6407C23.6565 16.0741 23.387 16.4942 23.0599 16.8732C22.8984 17.0623 22.7179 17.2302 22.5028 17.3602C22.145 17.5753 21.7636 17.6124 21.368 17.5099C21.1924 17.461 21.0211 17.3978 20.8558 17.3208C20.2175 17.034 19.5855 17.0434 18.9504 17.3263C18.7274 17.4264 18.4996 17.5202 18.2561 17.5659C17.8858 17.6352 17.5564 17.5485 17.253 17.3303C16.948 17.1112 16.7116 16.8283 16.4894 16.5304C16.1348 16.0537 15.818 15.554 15.5824 15.0064C15.2931 14.338 15.1056 13.6301 15.026 12.9063C15.0181 12.833 15.0087 12.7605 15 12.6872V11.9149V11.9149ZM22.0119 6C22.0142 6.09456 22.0197 6.18913 22.0174 6.2829C22.004 6.82822 21.8046 7.31128 21.491 7.74706C21.1726 8.1915 20.7502 8.49095 20.2128 8.61783C20.0354 8.65704 19.8526 8.66584 19.6722 8.64383C19.6382 8.419 19.653 8.18947 19.7156 7.97086C20.0126 6.92436 20.699 6.28448 21.7518 6.03231C21.8101 6.01891 21.8708 6.01024 21.9307 6H22.0119V6ZM27.9418 13.822V17.4445H26.2869V6.81324H30.2523C30.8937 6.81324 31.5068 6.93539 32.075 7.24272C32.7028 7.57468 33.1983 8.11132 33.4792 8.76361C33.7393 9.35148 33.8228 9.97008 33.7795 10.6076C33.7401 11.1907 33.5833 11.7384 33.2751 12.238C32.8819 12.8755 32.3256 13.3137 31.6274 13.5777C31.164 13.7526 30.681 13.8204 30.1884 13.822H27.9426H27.9418ZM27.9166 12.4429H27.9733C28.5935 12.4429 29.2136 12.4453 29.8346 12.4421C30.0978 12.4405 30.3571 12.4153 30.6116 12.3476C31.2696 12.1742 31.7385 11.7873 31.9576 11.1301C32.101 10.7014 32.1073 10.2609 32.0285 9.81957C31.9087 9.14029 31.5352 8.65723 30.8866 8.39954C30.5375 8.26164 30.1703 8.22381 29.7984 8.22224H27.9166V12.4437V12.4429ZM34.5533 12.0158C34.5604 11.7896 34.6038 11.5745 34.6794 11.3657C34.93 10.6659 35.4241 10.1978 36.09 9.89837C36.5313 9.70136 36.9962 9.60838 37.4793 9.58158C38.0474 9.55006 38.6061 9.6005 39.1452 9.78884C39.8441 10.0308 40.38 10.4587 40.67 11.1584C40.8024 11.4784 40.8528 11.8141 40.8528 12.1569C40.8552 13.9094 40.8536 15.6628 40.8536 17.4146V17.4729H39.3327V16.2113L39.3162 16.2081C39.3083 16.2176 39.2972 16.2254 39.2917 16.2365C38.9261 16.8937 38.3658 17.3058 37.6511 17.5013C36.8102 17.7298 35.9962 17.6447 35.2468 17.1884C34.655 16.8283 34.3216 16.2853 34.235 15.5966C34.1837 15.1931 34.2176 14.8007 34.38 14.424C34.5659 13.9945 34.878 13.6833 35.2783 13.4563C35.6982 13.2192 36.1637 13.0739 36.644 13.03C37.1798 12.9772 37.718 12.9528 38.2555 12.9189C38.5754 12.8984 38.8946 12.8834 39.2161 12.8645C39.224 12.8645 39.2334 12.8621 39.25 12.859V12.684C39.2421 12.45 39.2547 12.2136 39.2184 11.9827C39.1333 11.4334 38.7921 11.1033 38.2641 10.9654C37.8384 10.8509 37.388 10.8674 36.9718 11.0126C36.5108 11.1702 36.2066 11.4839 36.086 11.963C36.0766 12.0008 36.0671 12.0253 36.0183 12.0253C35.547 12.0221 35.075 12.0237 34.6038 12.0237C34.587 12.0206 34.5702 12.0179 34.5533 12.0158V12.0158ZM39.25 13.9622C39.22 13.9622 39.2027 13.9607 39.1846 13.9622C38.5321 14.0016 37.8796 14.0379 37.2271 14.0844C36.931 14.0996 36.6419 14.1803 36.3808 14.3208C35.841 14.6202 35.7377 15.1971 35.9371 15.6604C36.0845 16.0056 36.3729 16.1947 36.7235 16.2838C37.2596 16.426 37.8299 16.3557 38.3154 16.0875C38.8055 15.8251 39.127 15.4248 39.2216 14.8692C39.2547 14.673 39.2429 14.4697 39.2492 14.2703C39.2523 14.1695 39.2492 14.0678 39.2492 13.963L39.25 13.9622ZM42.2673 20.3492V19.0781C42.4722 19.0781 42.6755 19.0828 42.8804 19.0765C43.1806 19.0671 43.4596 18.9914 43.6881 18.7826C43.8517 18.6319 43.9761 18.4436 44.0506 18.2341C44.1365 18.004 44.2059 17.7692 44.2776 17.5344C44.2855 17.5076 44.2744 17.4721 44.265 17.4437C43.8363 16.2578 43.4068 15.0718 42.9765 13.8874L41.4982 9.81011C41.4911 9.7912 41.4856 9.77229 41.4777 9.74628C41.5061 9.74471 41.5305 9.74313 41.5534 9.74313C42.075 9.74313 42.5959 9.74471 43.1168 9.74234C43.1712 9.74234 43.1956 9.75731 43.2114 9.81011C43.852 11.8747 44.4959 13.9394 45.1365 16.004L45.1625 16.0749H45.1814C45.1893 16.0537 45.1996 16.0332 45.2059 16.0119L46.6921 11.238C46.8418 10.7605 46.99 10.2837 47.1381 9.80617C47.1499 9.76519 47.1618 9.74234 47.2138 9.74234C47.7378 9.74471 48.2603 9.74392 48.7835 9.74392C48.8016 9.74392 48.819 9.74628 48.8434 9.74865L48.6582 10.2743L45.9506 17.9213C45.7945 18.3618 45.6149 18.792 45.3658 19.1884C45.1271 19.5667 44.8363 19.8898 44.4328 20.0986C44.1328 20.2513 43.8061 20.3444 43.4706 20.3728C43.3028 20.3894 43.1318 20.4044 42.9639 20.3988C42.7315 20.3949 42.5014 20.3673 42.2673 20.35V20.3492Z"
                        fill="black"
                      />
                    </svg>
                  </button>
                  <button type="button" className="btn btn--secondary btn--express-payment google">
                    <svg width="64" height="24" viewBox="0 0 64 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M22.2571 9.26888C21.6414 8.67947 20.8184 8.35688 19.9674 8.37111C18.4115 8.37111 17.09 9.42353 16.6172 10.8428C16.3684 11.5863 16.3684 12.3914 16.6172 13.1357C17.09 14.5542 18.4107 15.6073 19.9674 15.6073C20.7738 15.6073 21.4644 15.3999 21.9977 15.0339C22.2027 14.8976 22.388 14.7348 22.5499 14.5502C22.6331 14.4545 22.7102 14.3532 22.7798 14.2466C22.8449 14.1475 22.9031 14.0446 22.9546 13.9386C23.0658 13.7097 23.1452 13.4661 23.1906 13.2142H19.9667V10.9073H25.5945C25.6361 11.1489 25.6648 11.3921 25.6815 11.6364C25.6875 11.718 25.6921 11.7996 25.6951 11.8813C25.6974 11.9626 25.6981 12.0438 25.6981 12.1252C25.6981 13.9481 25.0484 15.4893 23.9228 16.5317C22.9341 17.4473 21.5786 17.9773 19.9674 17.9773C18.8615 17.9765 17.7768 17.6678 16.8351 17.0852C15.8941 16.5026 15.1324 15.6688 14.6354 14.6769C14.5378 14.4819 14.4508 14.2818 14.3752 14.0779C14.267 13.7866 14.1815 13.4871 14.1195 13.1827C14.0802 12.9905 14.0507 12.7962 14.031 12.6005C14.0106 12.3979 14 12.1939 14 11.9891C14 11.0553 14.2179 10.1342 14.6354 9.3C15.1324 8.30811 15.8941 7.47429 16.8351 6.89171C17.7768 6.30914 18.8615 6.0004 19.9674 6.0004C21.4493 5.98294 22.8804 6.5416 23.9606 7.55968L22.2571 9.26888Z"
                        fill="black"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M31.0907 13.0355V17.256H29.7564V6.83289H33.2964C33.4106 6.83061 33.5249 6.83422 33.6383 6.84408C33.7246 6.85148 33.8108 6.86249 33.897 6.8771C34.1134 6.91392 34.3259 6.97312 34.5317 7.05396C34.925 7.20862 35.2836 7.43994 35.5876 7.73445C35.894 8.01151 36.1383 8.35118 36.3032 8.73071C36.3804 8.90738 36.4394 9.09069 36.4795 9.27799C36.5256 9.49318 36.5475 9.71368 36.5445 9.93495C36.5536 10.3513 36.4727 10.7646 36.307 11.1464C36.1421 11.5284 35.8963 11.87 35.5876 12.1478C35.3183 12.4047 35.0226 12.6059 34.6996 12.7512C34.2783 12.9408 33.81 13.0355 33.2957 13.0355H31.0907ZM31.0907 8.11531V11.7546H33.3289L33.4129 11.7552L33.4991 11.7514L33.596 11.7421C33.6625 11.7337 33.7291 11.7218 33.7942 11.706C33.8796 11.6853 33.9643 11.658 34.046 11.6246C34.273 11.5318 34.4787 11.3923 34.6497 11.2151C34.7472 11.1196 34.8335 11.0134 34.9069 10.8984C34.9606 10.8141 35.0067 10.7253 35.046 10.6329C35.1383 10.4141 35.186 10.179 35.186 9.94159C35.186 9.8376 35.1769 9.73418 35.1587 9.63227C35.1353 9.50134 35.0982 9.37325 35.046 9.25028C34.9538 9.03148 34.8191 8.83356 34.6497 8.6679C34.6126 8.62862 34.574 8.59104 34.5339 8.55537C34.4772 8.50451 34.4174 8.45745 34.3539 8.41456C34.3017 8.37927 34.248 8.34663 34.1928 8.31703C34.1459 8.29217 34.0975 8.26939 34.0483 8.24871C34.012 8.23334 33.9749 8.2193 33.9371 8.20639L33.8327 8.17432C33.7299 8.14643 33.6247 8.12802 33.5181 8.11948C33.4553 8.11436 33.3925 8.11265 33.3289 8.11455L31.0907 8.11531Z"
                        fill="black"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M39.6201 9.8913C40.6065 9.8913 41.3849 10.1556 41.956 10.6841C42.5271 11.2136 42.8123 11.9387 42.8107 12.8605V17.2568H41.5347V16.2666H41.4764C40.9242 17.082 40.189 17.4892 39.2722 17.4892C38.4893 17.4892 37.8335 17.256 37.307 16.7905C37.0543 16.5767 36.8516 16.3089 36.7147 16.0068C36.6383 15.8389 36.5838 15.6626 36.5513 15.4819C36.5377 15.4058 36.5279 15.3292 36.5226 15.2519C36.5173 15.1828 36.515 15.1134 36.5158 15.0439C36.5158 14.3056 36.7941 13.7186 37.3494 13.2833C37.5506 13.1256 37.776 12.9964 38.0264 12.8956C38.4674 12.7178 38.9832 12.6284 39.5763 12.6273C40.369 12.6273 41.021 12.7734 41.5339 13.0641V12.7579C41.5354 12.5319 41.487 12.3083 41.3917 12.1034L41.3705 12.0599L41.3372 11.9971C41.3213 11.9684 41.3039 11.9404 41.2858 11.9128L41.2427 11.85C41.1678 11.7476 41.0808 11.6546 40.9832 11.5734C40.63 11.2536 40.1701 11.079 39.695 11.0845C38.9492 11.0845 38.3599 11.4001 37.925 12.0313L36.7495 11.2889C37.3963 10.3562 38.3524 9.8913 39.6201 9.8913ZM37.894 15.0734C37.8932 15.2438 37.9333 15.4119 38.0105 15.5637C38.0876 15.7155 38.1996 15.8466 38.3372 15.9463C38.6315 16.1795 38.9984 16.303 39.3735 16.296C39.9371 16.295 40.4764 16.07 40.8743 15.6703C41.3168 15.2531 41.5384 14.7625 41.5384 14.2006C41.1216 13.868 40.5415 13.7016 39.7971 13.7016C39.2548 13.7016 38.8032 13.8329 38.4416 14.0949C38.0763 14.3615 37.894 14.6847 37.894 15.0734Z"
                        fill="black"
                      />
                      <path
                        d="M50.139 10.1245L45.6821 20.4H44.3047L45.9582 16.8051L43.0278 10.1238H44.4787L46.5967 15.2476H46.6262L48.6859 10.1245H50.139Z"
                        fill="black"
                      />
                    </svg>
                  </button>
                  <button type="button" className="btn btn--secondary btn--express-payment paypal">
                    <svg width="64" height="24" viewBox="0 0 64 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clip-path="url(#clip0)">
                        <path
                          d="M25.6835 7.86322H22.7705C22.674 7.86316 22.5808 7.89741 22.5074 7.95979C22.4341 8.02217 22.3855 8.10858 22.3705 8.20346L21.1924 15.6434C21.1869 15.6779 21.1891 15.7132 21.1987 15.7468C21.2083 15.7804 21.2251 15.8116 21.2479 15.8381C21.2707 15.8646 21.2991 15.8859 21.331 15.9005C21.3628 15.9151 21.3975 15.9226 21.4326 15.9226H22.8233C22.9198 15.9226 23.0131 15.8883 23.0865 15.8258C23.1598 15.7634 23.2083 15.6768 23.2232 15.5819L23.541 13.5752C23.5559 13.4803 23.6044 13.3939 23.6776 13.3314C23.7509 13.269 23.8441 13.2346 23.9405 13.2346H24.8627C26.7816 13.2346 27.889 12.3097 28.1782 10.477C28.3086 9.67516 28.1838 9.04516 27.8068 8.60395C27.3928 8.11946 26.6585 7.86322 25.6835 7.86322V7.86322ZM26.0195 10.5805C25.8602 11.6216 25.0616 11.6216 24.2894 11.6216H23.8498L24.1582 9.67728C24.1672 9.6204 24.1963 9.56859 24.2403 9.53118C24.2842 9.49378 24.3402 9.47322 24.398 9.47322H24.5995C25.1255 9.47322 25.6217 9.47322 25.8781 9.77189C26.031 9.95007 26.0779 10.2148 26.0195 10.5805V10.5805ZM34.391 10.547H32.996C32.9382 10.547 32.8823 10.5675 32.8383 10.6049C32.7944 10.6423 32.7653 10.6942 32.7562 10.751L32.6945 11.1396L32.5969 10.9988C32.2949 10.5623 31.6215 10.4163 30.9494 10.4163C29.4079 10.4163 28.0913 11.5792 27.8349 13.2104C27.7016 14.0241 27.8911 14.8021 28.3546 15.3447C28.7796 15.8436 29.3879 16.0515 30.1116 16.0515C31.3536 16.0515 32.0423 15.2561 32.0423 15.2561L31.9802 15.6421C31.9746 15.6766 31.9767 15.7119 31.9861 15.7456C31.9956 15.7793 32.0123 15.8105 32.035 15.8371C32.0578 15.8637 32.0861 15.8851 32.1179 15.8998C32.1498 15.9144 32.1844 15.9221 32.2195 15.9221H33.4761C33.5726 15.9222 33.6659 15.8879 33.7392 15.8254C33.8126 15.7629 33.8611 15.6764 33.876 15.5815L34.6299 10.8261C34.6355 10.7917 34.6335 10.7565 34.624 10.7229C34.6145 10.6893 34.5979 10.6582 34.5752 10.6316C34.5524 10.6051 34.5242 10.5838 34.4924 10.5691C34.4606 10.5545 34.426 10.547 34.391 10.547V10.547ZM32.4466 13.2511C32.312 14.0449 31.6794 14.5777 30.8727 14.5777C30.4676 14.5777 30.1439 14.4483 29.9361 14.2031C29.7299 13.9596 29.6515 13.613 29.7171 13.2269C29.8428 12.4399 30.486 11.8897 31.2803 11.8897C31.6765 11.8897 31.9985 12.0208 32.2106 12.2681C32.4231 12.518 32.5075 12.8667 32.4466 13.2511V13.2511ZM41.8202 10.547H40.4185C40.3524 10.5471 40.2873 10.5632 40.2289 10.5941C40.1704 10.6249 40.1205 10.6695 40.0832 10.7239L38.1499 13.5604L37.3304 10.8346C37.3052 10.7515 37.2539 10.6786 37.1839 10.6268C37.114 10.575 37.0291 10.547 36.9419 10.547H35.5644C35.5258 10.5469 35.4876 10.556 35.4532 10.5735C35.4188 10.5911 35.3891 10.6165 35.3665 10.6478C35.344 10.6791 35.3293 10.7153 35.3236 10.7534C35.318 10.7915 35.3215 10.8304 35.334 10.8669L36.878 15.3799L35.4264 17.421C35.4006 17.4572 35.3853 17.4997 35.3822 17.544C35.3791 17.5883 35.3883 17.6325 35.4087 17.672C35.4292 17.7114 35.4601 17.7445 35.4982 17.7675C35.5362 17.7906 35.5799 17.8028 35.6245 17.8028H37.0246C37.0899 17.8029 37.1544 17.7872 37.2123 17.757C37.2702 17.7269 37.32 17.6832 37.3572 17.6297L42.0196 10.9267C42.0448 10.8904 42.0597 10.8479 42.0624 10.8039C42.0652 10.7598 42.0558 10.7159 42.0353 10.6767C42.0147 10.6376 41.9838 10.6049 41.9459 10.582C41.908 10.5591 41.8646 10.547 41.8202 10.547V10.547Z"
                          fill="black"
                        />
                        <path
                          d="M46.4609 7.86322H43.5475C43.4511 7.86326 43.3579 7.89755 43.2847 7.95992C43.2114 8.02229 43.1629 8.10865 43.1479 8.20346L41.9698 15.6434C41.9643 15.6779 41.9664 15.7131 41.9759 15.7467C41.9854 15.7803 42.0021 15.8114 42.0249 15.8379C42.0476 15.8645 42.0759 15.8858 42.1077 15.9004C42.1395 15.915 42.1741 15.9226 42.2092 15.9226H43.7042C43.7717 15.9225 43.8369 15.8984 43.8881 15.8547C43.9393 15.811 43.9732 15.7505 43.9836 15.6841L44.318 13.5752C44.3329 13.4803 44.3814 13.3939 44.4546 13.3314C44.5279 13.269 44.6211 13.2346 44.7175 13.2345H45.6393C47.5585 13.2345 48.6656 12.3097 48.9552 10.477C49.086 9.67516 48.9603 9.04516 48.5834 8.60394C48.1698 8.11946 47.4359 7.86322 46.4609 7.86322V7.86322ZM46.797 10.5805C46.6381 11.6216 45.8394 11.6216 45.0668 11.6216H44.6276L44.9365 9.67728C44.9453 9.62039 44.9743 9.56854 45.0182 9.53111C45.0621 9.49368 45.118 9.47315 45.1758 9.47322H45.3773C45.9029 9.47322 46.3996 9.47322 46.656 9.77188C46.8089 9.95007 46.8553 10.2148 46.797 10.5805V10.5805ZM55.168 10.547H53.7739C53.716 10.5468 53.6601 10.5673 53.6162 10.6048C53.5722 10.6422 53.5433 10.6941 53.5345 10.751L53.4727 11.1396L53.3748 10.9988C53.0728 10.5622 52.3998 10.4163 51.7276 10.4163C50.1862 10.4163 48.87 11.5792 48.6136 13.2104C48.4807 14.0241 48.6694 14.8021 49.1328 15.3447C49.5588 15.8436 50.1661 16.0515 50.8898 16.0515C52.1319 16.0515 52.8206 15.2561 52.8206 15.2561L52.7584 15.6421C52.7529 15.6767 52.7549 15.7121 52.7645 15.7458C52.774 15.7795 52.7907 15.8108 52.8136 15.8374C52.8364 15.864 52.8648 15.8854 52.8968 15.9C52.9287 15.9147 52.9635 15.9222 52.9987 15.9221H54.2548C54.3512 15.9221 54.4444 15.8877 54.5177 15.8253C54.5909 15.7628 54.6394 15.6763 54.6543 15.5815L55.4086 10.8261C55.414 10.7916 55.4117 10.7563 55.4021 10.7227C55.3925 10.6891 55.3756 10.6579 55.3528 10.6314C55.3299 10.6049 55.3016 10.5836 55.2697 10.569C55.2378 10.5545 55.2031 10.5469 55.168 10.547V10.547ZM53.2236 13.2511C53.0898 14.0449 52.4564 14.5777 51.6497 14.5777C51.2455 14.5777 50.9209 14.4483 50.7131 14.2031C50.5069 13.9596 50.4294 13.613 50.4941 13.2269C50.6206 12.4399 51.2629 11.8897 52.0573 11.8897C52.4534 11.8897 52.7755 12.0208 52.9876 12.2681C53.201 12.518 53.2853 12.8667 53.2236 13.2511V13.2511ZM56.8125 8.06728L55.6169 15.6434C55.6114 15.6779 55.6135 15.7131 55.623 15.7467C55.6325 15.7803 55.6492 15.8114 55.672 15.8379C55.6948 15.8645 55.723 15.8858 55.7548 15.9004C55.7866 15.915 55.8213 15.9226 55.8563 15.9226H57.0583C57.2581 15.9226 57.4276 15.7783 57.4582 15.5819L58.6373 8.14237C58.6427 8.10788 58.6406 8.07263 58.6311 8.03902C58.6216 8.00542 58.6049 7.97426 58.5822 7.9477C58.5594 7.92113 58.5312 7.89979 58.4994 7.88513C58.4676 7.87048 58.4329 7.86285 58.3979 7.86279H57.0519C56.9941 7.863 56.9383 7.88369 56.8944 7.92116C56.8506 7.95863 56.8215 8.01043 56.8125 8.06728Z"
                          fill="#6B6B6B"
                        />
                        <path
                          d="M15.8179 8.25269C15.806 8.32862 15.7924 8.40626 15.777 8.48602C15.2501 11.1804 13.4476 12.1112 11.1454 12.1112H9.97317C9.69162 12.1112 9.45437 12.3148 9.4105 12.5914L8.81035 16.3824L8.6404 17.457C8.63363 17.4997 8.63622 17.5433 8.64798 17.5848C8.65974 17.6263 8.6804 17.6648 8.70853 17.6976C8.73667 17.7305 8.77161 17.7568 8.81095 17.7749C8.85029 17.793 8.8931 17.8024 8.93643 17.8024H11.0154C11.2616 17.8024 11.4708 17.6242 11.5095 17.3824L11.53 17.2772L11.9214 14.803L11.9466 14.6672C11.9849 14.4246 12.1944 14.2464 12.4406 14.2464H12.7516C14.7659 14.2464 16.3427 13.4318 16.8035 11.0747C16.9961 10.0901 16.8964 9.2679 16.387 8.68966C16.2255 8.51078 16.0327 8.3627 15.8179 8.25269V8.25269Z"
                          fill="#C1C1C1"
                        />
                        <path
                          d="M15.2645 8.03373C15.0962 7.98526 14.9251 7.94742 14.7521 7.92046C14.4102 7.86812 14.0646 7.84301 13.7187 7.84537H10.5872C10.468 7.84527 10.3528 7.88765 10.2622 7.96484C10.1717 8.04202 10.1118 8.14892 10.0935 8.26621L9.42737 12.4688L9.4082 12.5914C9.42896 12.4576 9.4971 12.3356 9.6003 12.2475C9.70351 12.1594 9.83496 12.1111 9.97087 12.1111H11.1431C13.4453 12.1111 15.2478 11.1799 15.7747 8.48597C15.7905 8.40621 15.8037 8.32858 15.8156 8.25264C15.6766 8.18002 15.5317 8.11914 15.3824 8.07064C15.3433 8.05771 15.304 8.0454 15.2645 8.03373V8.03373Z"
                          fill="#6B6B6B"
                        />
                        <path
                          d="M10.095 8.26624C10.1131 8.14892 10.1729 8.04197 10.2635 7.96482C10.3541 7.88767 10.4694 7.84545 10.5886 7.84582H13.7201C14.0911 7.84582 14.4374 7.87 14.7535 7.92091C14.9673 7.95439 15.1782 8.00446 15.3843 8.07067C15.5397 8.122 15.6841 8.18267 15.8175 8.25267C15.9742 7.25697 15.8162 6.57903 15.2757 5.96515C14.6798 5.28933 13.6043 5 12.2281 5H8.23274C7.95162 5 7.71182 5.20364 7.66837 5.48067L6.00422 15.987C5.99647 16.0358 5.99942 16.0857 6.01287 16.1332C6.02631 16.1808 6.04994 16.2248 6.08212 16.2624C6.11429 16.3 6.15426 16.3302 6.19927 16.3509C6.24428 16.3716 6.29326 16.3824 6.34284 16.3824H8.80946L9.42878 12.4688L10.095 8.26624V8.26624Z"
                          fill="black"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0">
                          <rect width="52.8166" height="14" fill="white" transform="translate(6 5)" />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                </div>
              </>
            )} */}
            {isCartPage && (
              <a href="/collections/all" className="side-cart__continue-link">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 8H12.6667" stroke="black" stroke-linecap="square" />
                  <path d="M6.66732 4.66666L3.33398 8" stroke="black" stroke-linecap="square" />
                  <path d="M6.66732 11.3333L3.33398 8" stroke="black" stroke-linecap="square" />
                </svg>
                <span>{window.theme.strings.continue_shopping}</span>
              </a>
            )}
          </div>
        </div>
      </>
    );
  }
}

FreeShippingBar.propTypes = {
  total: PropTypes.number,
  threshold1: PropTypes.number,
  threshold2: PropTypes.number,
  freeShippingText: PropTypes.string,
  freeExpressShippingText: PropTypes.string,
  threshold1Text: PropTypes.string,
  threshold2Text: PropTypes.string,
};

export default SideCart;
/* eslint-enable */
